import copyToClipboard from 'copy-to-clipboard';
import React from 'react';
import styled from 'styled-components';

import {toast} from '../../../../common/components/elements/Toast';
import {MOON_500} from '../../../../common/css/color.styles';
import {Button} from '../../../Button';
import {CodeEditor} from '../../../CodeEditor';
import {TooltipHint} from '../../../DraggablePopups';
import {
  type CellType,
  useColumnOrLocalViewMode,
} from '../Browse3/columnViewMode';
import {CellValueWithPopupOver, TooltipContent} from './CellValueWithPopupOver';
import {NumberListBarChart} from './NumberListBarChart';
import {formatNumber} from './numberListChartCommon';
import {NumberListHistogram} from './NumberListHistogram';
import {summarize} from './numberListStats';

// Histogram is the default, showing the value distribution, readable at any
// length. Bars is the indexed bar-per-element chart, meaningful only for short
// lists. Text/Code render the raw JSON array as plain text or in a code
// viewer, matching how number lists rendered before this component existed.
const NUMBER_LIST_MODES = ['Histogram', 'Bars', 'Text', 'Code'] as const;
type NumberListViewMode = (typeof NUMBER_LIST_MODES)[number];

const DEFAULT_VIEW_MODE: NumberListViewMode = 'Histogram';

// Discriminates this cell's view-mode entries from other cell types sharing a
// column key (see `useColumnOrLocalViewMode`).
const CELL_TYPE: CellType = 'number-list';

// Above this length an indexed bar-per-element is unreadable and meaningless, so
// 'Bars' isn't offered and degrades to the histogram. Matches Panel2's bar
// chart, which is only offered for lists of maxLength 25.
const MAX_INDEXED_ITEMS = 25;

// Single-line, ellipsis-truncated raw text in the cell's default, proportional
// font — matches the old fallback rendering. Compact JSON has no spaces, so
// wrapping could only break mid-number; keep it on one line instead.
const NumberListText = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
`;
NumberListText.displayName = 'S.NumberListText';

const PoppedList = styled.div`
  max-height: 35vh;
  overflow: auto;
  white-space: break-spaces;
  font-family: monospace;
  font-size: 12px;
`;
PoppedList.displayName = 'S.PoppedList';

const StatsRow = styled.div`
  display: flex;
  gap: 12px;
  padding: 4px 0;
  font-size: 12px;
  color: ${MOON_500};
`;
StatsRow.displayName = 'S.StatsRow';

type CellValueNumberListProps = {
  value: number[];
  /**
   * Stable per-column identifier. View mode is shared across all cells with the
   * same key, so toggling one flips the whole column. Without it the toggle
   * falls back to per-cell local state.
   */
  columnKey?: string;
  /** Bar color (e.g. the eval's color); defaults to teal. */
  color?: string;
  /**
   * Stretch the chart to fill the cell's height, for tables that give these
   * rows extra room; otherwise it sizes to content.
   */
  tall?: boolean;
};

export const CellValueNumberList = ({
  value,
  columnKey,
  color,
  tall,
}: CellValueNumberListProps) => {
  // When a column key is available *and* a grid provider is present, the mode is
  // shared column-wide; otherwise it falls back to per-cell local state.
  const [mode, setMode, isColumnShared] =
    useColumnOrLocalViewMode<NumberListViewMode>(
      columnKey,
      CELL_TYPE,
      DEFAULT_VIEW_MODE,
      NUMBER_LIST_MODES
    );

  const copy = (e: React.MouseEvent) => {
    e.stopPropagation();
    copyToClipboard(JSON.stringify(value));
    toast('Copied to clipboard');
  };

  const isChartMode = mode === 'Histogram' || mode === 'Bars';
  // 'Bars' is a preference, honored only where an indexed chart is feasible.
  // The mode is column-wide, so a short cell setting 'Bars' must not force
  // 1000-bar renders on its neighbors — longer lists degrade to the histogram
  // instead.
  const barsFeasible = value.length <= MAX_INDEXED_ITEMS;
  const showBars = mode === 'Bars' && barsFeasible;
  const ChartComp = showBars ? NumberListBarChart : NumberListHistogram;

  let collapsed: React.ReactNode;
  if (isChartMode) {
    collapsed = (
      <div style={{height: '100%', width: '100%'}}>
        <ChartComp values={value} color={color} />
      </div>
    );
  } else {
    // Text and code both render as a single line of plain text in the cell,
    // like the old fallback; only the popover differs, showing plain text vs
    // a code editor.
    collapsed = <NumberListText>{JSON.stringify(value)}</NumberListText>;
  }

  const tooltip = (
    <TooltipContent>
      <div>{value.length} values</div>
      <TooltipHint>Click to view</TooltipHint>
    </TooltipContent>
  );

  const modeTooltip = isColumnShared ? ' (applies to whole column)' : '';
  const modeButton = (
    buttonMode: NumberListViewMode,
    icon: React.ComponentProps<typeof Button>['icon'],
    label: string,
    active: boolean,
    opts?: {disabled?: boolean; tooltip?: string}
  ) => (
    <Button
      size="small"
      variant="ghost"
      active={active}
      disabled={opts?.disabled}
      icon={icon}
      tooltip={opts?.tooltip ?? `${label}${modeTooltip}`}
      onClick={e => {
        e.stopPropagation();
        setMode(buttonMode);
      }}
    />
  );
  // Highlight the chart that's actually rendered, not just the stored mode, so
  // a 'Bars'-but-too-long cell shows Histogram as active.
  const leftToolbarButtons = (
    <>
      {modeButton(
        'Histogram',
        'chart-vertical-bars',
        'Histogram',
        isChartMode && !showBars
      )}
      {modeButton(
        'Bars',
        'chart-horizontal-bars',
        'Bar chart',
        showBars,
        barsFeasible
          ? undefined
          : {
              disabled: true,
              tooltip: `Bar chart is only available for ${MAX_INDEXED_ITEMS} or fewer values`,
            }
      )}
      {modeButton('Text', 'text-language', 'Text', mode === 'Text')}
      {modeButton('Code', 'code-alt', 'Code', mode === 'Code')}
    </>
  );

  const rightToolbarButtons = (
    <Button
      size="small"
      variant="ghost"
      icon="copy"
      tooltip="Copy to clipboard"
      onClick={copy}
    />
  );

  // Indexed bars grow one row per element, within bounds; the histogram has a
  // fixed bin count, so a fixed height reads well.
  const popoverChartHeight = showBars
    ? Math.min(280, Math.max(120, value.length * 16 + 24))
    : 200;
  let popoverContent: React.ReactNode;
  if (isChartMode) {
    popoverContent = (
      <>
        <div style={{height: popoverChartHeight, width: '100%'}}>
          <ChartComp values={value} color={color} />
        </div>
        <NumberListStats values={value} />
      </>
    );
  } else if (mode === 'Code') {
    popoverContent = (
      <>
        <NumberListStats values={value} />
        <NumberListCode values={value} />
      </>
    );
  } else {
    popoverContent = (
      <>
        <NumberListStats values={value} />
        <NumberListRaw values={value} />
      </>
    );
  }
  // Fill PoppedBody's fixed width so the chart/code spans the whole popover
  // instead of a narrow column; recharts' ResponsiveContainer and the code
  // editor measure against that definite width.
  const popoverBody = <div style={{width: '100%'}}>{popoverContent}</div>;

  return (
    <CellValueWithPopupOver
      collapsed={collapsed}
      // Chart modes already show a per-bar hover tooltip, so suppress the
      // wrapper's "click to view" hint to avoid two tooltips on one cell.
      tooltip={isChartMode ? undefined : tooltip}
      leftToolbarButtons={leftToolbarButtons}
      rightToolbarButtons={rightToolbarButtons}
      popoverBody={popoverBody}
      fullHeight={tall}
    />
  );
};

const NumberListStats = ({values}: {values: number[]}) => {
  if (values.length === 0) {
    return null;
  }
  const {min, max, mean, count} = summarize(values);
  return (
    <StatsRow>
      <span>n {count}</span>
      <span>min {formatNumber(min)}</span>
      <span>max {formatNumber(max)}</span>
      <span>mean {formatNumber(mean)}</span>
    </StatsRow>
  );
};

// Popover-body views live in components (not inline JSX) so the pretty-print —
// like the stats above — runs only when the popover opens, not once per
// collapsed cell.
const NumberListCode = ({values}: {values: number[]}) => (
  <CodeEditor
    value={JSON.stringify(values, null, 2)}
    language="json"
    readOnly
    maxHeight={280}
    handleMouseWheel
    alwaysConsumeMouseWheel={false}
  />
);

const NumberListRaw = ({values}: {values: number[]}) => (
  <PoppedList>{JSON.stringify(values, null, 2)}</PoppedList>
);
